import React, { useState, useEffect } from "react";
import {
  X,
  Barbell,
  Heartbeat,
  Receipt,
  Bell,
  ChartLineUp,
  CaretRight,
  CaretLeft,
  CheckCircle,
  Sparkle,
} from "@phosphor-icons/react";
import api from "../lib/api";

const FEATURES = [
  {
    icon: Sparkle,
    color: "#FF3B30",
    title: "¡Bienvenido a Athlon!",
    body: "Te mostramos en 30 segundos las 5 funciones que vas a usar más.",
    bg: "from-[#FF3B30]/20",
  },
  {
    icon: Barbell,
    color: "#FFD60A",
    title: "Tus rutinas del día",
    body: "Cada día tenés tu entrenamiento listo. Apretá 'Iniciar Sesión' y la app te guía serie por serie con timers automáticos, RPE y registro de cargas.",
    cta: "Ver mis rutinas",
    route: "?tab=routines",
    bg: "from-[#FFD60A]/15",
  },
  {
    icon: Heartbeat,
    color: "#32D74B",
    title: "Wellness diario",
    body: "Reportá sueño, energía y dolor muscular cada mañana. Tu entrenador ve si estás listo para entrenar fuerte o necesitás bajar la carga.",
    cta: "Cargar wellness",
    route: "?tab=wellness",
    bg: "from-[#32D74B]/15",
  },
  {
    icon: ChartLineUp,
    color: "#5E5CE6",
    title: "Testeos físicos",
    body: "Salto vertical, agilidad, fuerza máxima, etc. Cargás tus tests y la app dibuja tu evolución en gráficos. Comparás contra tu mejor versión.",
    cta: "Ver testeos",
    route: "?tab=tests",
    bg: "from-[#5E5CE6]/15",
  },
  {
    icon: Receipt,
    color: "#009EE3",
    title: "Pagos y resumen",
    body: "Pagás la cuota desde la app con Mercado Pago, transferencia o efectivo. Tu historial completo de pagos queda con PDF descargable.",
    cta: "Ver mi resumen",
    route: "?tab=payments-summary",
    bg: "from-[#009EE3]/15",
  },
  {
    icon: Bell,
    color: "#FF9500",
    title: "Notificaciones",
    body: "Te avisamos cuando tu entrenador suba una rutina nueva, cuando tengas que reportar wellness y cuando confirme tu pago.",
    cta: "Empezar a entrenar",
    bg: "from-[#FF9500]/15",
  },
];

/**
 * Tour in-app que se muestra una vez después del onboarding.
 *
 * Persiste en `user.profile.tour_completed_at` vía PUT /users/{id}. Si el campo
 * existe, el tour no vuelve a aparecer (a menos que el atleta lo abra
 * manualmente desde algún botón).
 */
export default function FeatureTour({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  const total = FEATURES.length;
  const current = FEATURES[step];
  const Icon = current.icon;

  const persistCompletion = async () => {
    try {
      await api.put(`/users/${user.user_id}`, {
        profile: {
          ...(user.profile || {}),
          tour_completed_at: new Date().toISOString(),
        },
      });
    } catch {
      /* silent — el tour se cierra igual, se podrá reabrir si vuelve */
    }
  };

  const skip = async () => {
    setClosing(true);
    await persistCompletion();
    onComplete?.();
  };

  const next = () => {
    if (step < total - 1) setStep((s) => s + 1);
    else skip();
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  useEffect(() => {
    // Bloquear scroll mientras el tour está activo
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);

  if (closing) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-4" data-testid="feature-tour">
      <div className="w-full max-w-md card-base p-0 overflow-hidden relative">
        {/* Progress dots */}
        <div className="absolute top-4 left-4 right-12 flex gap-1.5" data-testid="tour-progress">
          {FEATURES.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 transition-all duration-300 ${i === step ? "bg-primary-app" : i < step ? "bg-[#404040]" : "bg-[#1a1a1a]"}`}
            />
          ))}
        </div>

        <button
          onClick={skip}
          className="absolute top-3 right-3 text-muted hover:text-white p-1"
          data-testid="tour-skip"
          aria-label="Cerrar tutorial"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Hero */}
        <div className={`bg-gradient-to-br ${current.bg} to-transparent px-6 pt-12 pb-6 flex flex-col items-center text-center`}>
          <div
            className="w-16 h-16 flex items-center justify-center mb-4 border border-app"
            style={{ backgroundColor: "#0A0A0A" }}
          >
            <Icon size={32} weight="duotone" style={{ color: current.color }} />
          </div>
          <h2 className="font-display font-black uppercase text-2xl tracking-tighter mb-2" data-testid="tour-title">
            {current.title}
          </h2>
          <p className="text-sm text-muted leading-relaxed max-w-sm" data-testid="tour-body">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <button
            onClick={back}
            disabled={step === 0}
            className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="tour-back"
          >
            <CaretLeft size={12} weight="bold" /> Atrás
          </button>
          <span className="text-[10px] uppercase tracking-widest text-muted">
            {step + 1} / {total}
          </span>
          <button
            onClick={next}
            className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2"
            data-testid="tour-next"
          >
            {step === total - 1 ? (
              <>Empezar <CheckCircle size={14} weight="bold" /></>
            ) : (
              <>{current.cta || "Siguiente"} <CaretRight size={12} weight="bold" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
